import { useCallback } from 'react';
import { useComposerContext } from '../context/ComposerContext';
import { useBlockStyles } from '../context/BlockStylesContext';
import { ROOT_ID, PRESETS } from '../config/presets';
import { normalizeDesignPayload } from '../utils/designPayload';
import { normalizeFooterPayload } from '../utils/footerPayload';

export default function useDraftPersistence({ ensureRoot, setEditorHtml }) {
  const ctx = useComposerContext();
  const { blockStyles, setBlockStyles } = useBlockStyles();
  const capture = useCallback(() => ({
    templateVersion: ctx.templateVersion, newsletterId: ctx.newsletterId, version: ctx.version, subject: ctx.subject, recipients: ctx.recipients,
    selectedList: ctx.selectedList, preheaderText: ctx.preheaderText, body: ctx.editorRef.current?.innerHTML || '',
    footer: normalizeFooterPayload(ctx.footer), blockStyles, design: normalizeDesignPayload(ctx.getDesignFallback()),
  }), [ctx, blockStyles]);
  const restoreDocument = useCallback((d, template = false) => {
    if (!d || typeof d !== 'object' || typeof (d.body ?? d.bodyHtml) !== 'string') throw new Error('Saved document is unreadable.');
    if (!template) { ctx.setNewsletterId(d.newsletterId || null); ctx.setVersion(d.version || null); ctx.setRecipients(typeof d.recipients === 'string' ? d.recipients : ''); ctx.setSelectedList(d.selectedList || ''); }
    if (d.templateVersion) ctx.setTemplateVersion(d.templateVersion);
    ctx.setSubject(d.subject || ''); ctx.setPreheaderText(d.preheaderText || '');
    ctx.applyDesignState(normalizeDesignPayload(d.design || {}, PRESETS.find(p => p.key === 'bookish')));
    ctx.setFooter(normalizeFooterPayload(d.footer)); setBlockStyles(d.blockStyles || {});
    setEditorHtml(d.body ?? d.bodyHtml);
  }, [ctx, setBlockStyles, setEditorHtml]);
  const saveDraftLocal = useCallback((overrides = {}) => {
    if (!ctx.ready || !ctx.owner) throw new Error('Open your draft before saving.');
    ensureRoot();
    const data = { ...capture(), ...overrides, owner: ctx.owner, updatedAt: new Date().toISOString() };
    const base = ctx.draftKey.slice(0, ctx.draftKey.lastIndexOf('-' + (ctx.newsletterId || 'new')));
    const key = base + '-' + (data.newsletterId || 'new');
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(base + '-latest', key);
    return data;
  }, [ctx, capture, ensureRoot]);
  const restoreDraft = useCallback(() => {
    const base = ctx.draftKey.slice(0, ctx.draftKey.lastIndexOf('-' + (ctx.newsletterId || 'new')));
    const latest = localStorage.getItem(base + '-latest');
    const key = latest?.startsWith(base + '-') ? latest : ctx.draftKey;
    const raw = localStorage.getItem(key);
    if (raw) { const d = JSON.parse(raw); if (d.owner !== ctx.owner) throw new Error('This local draft belongs to a different account.'); restoreDocument(d); }
    else ensureRoot();
    ctx.setReady(true);
    return Boolean(raw);
  }, [ctx, restoreDocument, ensureRoot]);
  const clearDraft = useCallback(() => {
    if (ctx.operationLock.current) { ctx.setNotice("Wait for the current save or upload before starting a new draft."); return; }
    const bookish = PRESETS.find(p => p.key === 'bookish');
    ctx.setNewsletterId(null); ctx.setVersion(null); ctx.setSubject(''); ctx.setRecipients(''); ctx.setSelectedList(''); ctx.setPreheaderText('');
    ctx.setComposerMode('design'); ctx.setFooter(normalizeFooterPayload({ enabled: true })); ctx.applyDesignState(bookish); setBlockStyles({});
    setEditorHtml(`<div id="${ROOT_ID}"></div>`); localStorage.removeItem(ctx.draftKey); ctx.setReady(true);
  }, [ctx, setBlockStyles, setEditorHtml]);
  return { capture, restoreDocument, saveDraftLocal, restoreDraft, clearDraft };
}
